import { useContext, useRef } from 'react';
import { Row, Col } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useFormContext, Controller } from 'react-hook-form';
import { CheckInput } from '@octano/global-ui';

import { CourseFormContext } from '../../providers/CourseProvider';
import { SectionTitle } from '../../../../../components/text';
import {
  CourseAttendance,
  CourseFormFields,
  CourseFormMode,
} from '../../types';
import { TranslationsKeys } from '../../../../../locales/translations';

type CourseActivityTypeSectionProps = {
  readOnly?: boolean;
};

const CourseActivityTypeSection = ({
  readOnly = false,
}: CourseActivityTypeSectionProps) => {
  const { t } = useTranslation(TranslationsKeys.ACADEMIC_OFFER_COURSES_UPDATE);

  const { control } = useFormContext<CourseFormFields>();

  const { mode, formData, course } = useContext(CourseFormContext);
  const { activities = [] } = formData;

  /**
   * HU 15624 — Retención de valores en memoria (FRD §1.3.c / CP-19).
   *
   * Al desmarcar un componente snapshotteamos sus valores actuales aquí.
   * Al volver a marcarlo, restauramos minPercentage/totalHours/weeklyHours
   * antes de defaultear a los valores cargados desde BD (course?.assistance…).
   *
   * Vive en useRef porque sobrevive a re-renders pero se descarta cuando el
   * formulario se cierra — coherente con "durante la sesión de edición activa".
   */
  const detachedAttendanceRef = useRef<Map<number, CourseAttendance>>(
    new Map(),
  );

  if (!activities?.length) {
    return <></>;
  }

  return (
    <Row>
      <Col xs={12} className="pt-4">
        <SectionTitle text={t(`compositionSubsection`)} />
      </Col>
      <Col xs={12}>
        <p className="fs-18 fw-300">{t(`compositionDescription`)}</p>
      </Col>
      <Col xs={12}>
        <Row className="px-md-3 py-2">
          <Controller
            control={control}
            name="attendance"
            render={({ field }) => (
              <>
                {activities.map((activity) => (
                  <Col
                    xs={6}
                    md={4}
                    lg={3}
                    xl={2}
                    key={`activity_${activity.value}`}
                  >
                    <CheckInput
                      name={`activity_${activity.value}`}
                      label={activity.label}
                      onChange={() => {
                        const activityTypeId = +activity.value;
                        const current = field?.value ?? [];
                        const existing = current.find(
                          (e) => e.activityTypeId === activityTypeId,
                        );

                        if (!existing) {
                          // Marcar: prioridad de fuentes para valores iniciales
                          //   1) snapshot en memoria (CP-19)
                          //   2) requirement cargado de BD
                          //   3) defaults (0/null)
                          const cached = detachedAttendanceRef.current.get(
                            activityTypeId,
                          );
                          const loaded = course?.assistanceRequirements?.find(
                            (e) => e.activityTypeId === activityTypeId,
                          );
                          const restored: CourseAttendance = cached ?? {
                            activityTypeId,
                            minPercentage: loaded?.minPercentage ?? 0,
                            totalHours: loaded?.totalHours ?? null,
                            weeklyHours: loaded?.weeklyHours ?? null,
                          };
                          field?.onChange(current.concat(restored));
                        } else {
                          // Desmarcar: snapshot antes de filtrar para poder restaurar
                          detachedAttendanceRef.current.set(
                            activityTypeId,
                            existing,
                          );
                          field?.onChange(
                            current.filter(
                              (e) => e.activityTypeId !== activityTypeId,
                            ),
                          );
                        }
                      }}
                      value={
                        !!field?.value?.some(
                          (e) => e.activityTypeId === activity.value,
                        )
                      }
                      disabled={readOnly || mode === CourseFormMode.WATCH}
                    />
                  </Col>
                ))}
              </>
            )}
          />
        </Row>
      </Col>
    </Row>
  );
};

export default CourseActivityTypeSection;
